import { LINK_STATE_LABELS, countryName, type CreateLinkInput, type LinkState } from '@crm/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Check, Copy, ExternalLink, Printer, Trash2, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge, Card, Skeleton, type BadgeVariant } from '@/components/ui/field'
import { statusLabel, statusTone, jobCategoryOptions } from '@/features/clients/options'
import {
  linkQuery,
  publicUrlFor,
  registrantsQuery,
  useDeleteLink,
  useUpdateLink,
  type RegistrationLink,
} from '@/features/links/api'
import { LinkForm } from '@/features/links/link-form'
import { ApiClientError } from '@/lib/api'
import { formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../../../_app'

export const Route = createFileRoute('/_app/links/$linkId/')({
  component: LinkDetailPage,
})

const STATE_TONE: Record<LinkState, BadgeVariant> = {
  open: 'success',
  scheduled: 'default',
  closed: 'muted',
  capped: 'destructive',
  disabled: 'muted',
}

function LinkDetailPage() {
  const { linkId } = Route.useParams()
  const { me } = AppRoute.useRouteContext()
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)

  const { data, isLoading, isError } = useQuery(linkQuery(linkId))
  const link = data?.link

  if (isLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 p-8">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (isError || !link) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm font-medium">Link not found</p>
        <Link to="/links" className="mt-2 inline-block text-sm underline">
          Back to links
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Link
        to="/links"
        className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:underline"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to links
      </Link>

      <LinkHeader
        link={link}
        canEdit={me.permissions.includes('links:update')}
        canDelete={me.permissions.includes('links:delete')}
        editing={editing}
        onEdit={() => setEditing(true)}
        onDeleted={() => navigate({ to: '/links' })}
      />

      {editing ? (
        <EditLink link={link} onDone={() => setEditing(false)} />
      ) : (
        <Registrants linkId={linkId} />
      )}
    </div>
  )
}

function LinkHeader({
  link,
  canEdit,
  canDelete,
  editing,
  onEdit,
  onDeleted,
}: {
  link: RegistrationLink
  canEdit: boolean
  canDelete: boolean
  editing: boolean
  onEdit: () => void
  onDeleted: () => void
}) {
  const remove = useDeleteLink()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [copied, setCopied] = useState(false)
  const url = publicUrlFor(link.slug)

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold">{link.title}</h1>
            <Badge variant={STATE_TONE[link.state]}>{LINK_STATE_LABELS[link.state]}</Badge>
          </div>
          <p className="mt-1 font-mono text-xs text-[var(--color-muted-foreground)]">{url}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(url)
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              } catch {
                /* clipboard unavailable — URL is visible above */
              }
            }}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Link to="/links/$linkId/poster" params={{ linkId: link.id }} className="inline-flex">
            <Button variant="outline" size="sm">
              <Printer className="h-3.5 w-3.5" />
              Poster
            </Button>
          </Link>
          <a href={url} target="_blank" rel="noreferrer" className="inline-flex">
            <Button variant="outline" size="sm">
              <ExternalLink className="h-3.5 w-3.5" />
              Preview
            </Button>
          </a>
          {canEdit && !editing && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-xs text-[var(--color-muted-foreground)]">Registrations</dt>
          <dd className="font-medium">
            {link.registrationCount}
            {link.maxRegistrations !== null && ` / ${link.maxRegistrations}`}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-muted-foreground)]">Opens</dt>
          <dd className="font-medium">
            {link.opensAt ? formatDateTime(link.opensAt) : 'Immediately'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-muted-foreground)]">Closes</dt>
          <dd className="font-medium">
            {link.closesAt ? formatDateTime(link.closesAt) : 'No end date'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-[var(--color-muted-foreground)]">Auto-tags</dt>
          <dd className="font-medium">
            {[
              ...(link.presetCountries ?? []).map(countryName),
              ...(link.presetCategories ?? []).map(
                (id) => jobCategoryOptions.find((o) => o.value === id)?.label ?? id,
              ),
            ].join(', ') || 'None'}
          </dd>
        </div>
      </dl>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold">Delete this link?</h2>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              The URL stops working immediately. The {link.registrationCount} client
              {link.registrationCount === 1 ? '' : 's'} who registered through it are kept.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={remove.isPending}
                onClick={() => remove.mutate(link.id, { onSuccess: onDeleted })}
              >
                Delete
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Card>
  )
}

/** Maps the API row back onto the form's input shape. */
function toFormValues(link: RegistrationLink): Partial<CreateLinkInput> {
  return {
    title: link.title,
    description: link.description ?? undefined,
    slug: link.slug,
    // <input type="datetime-local"> wants `YYYY-MM-DDTHH:mm`, not a full ISO string.
    opensAt: link.opensAt ? link.opensAt.slice(0, 16) : undefined,
    closesAt: link.closesAt ? link.closesAt.slice(0, 16) : undefined,
    active: link.active,
    maxRegistrations: link.maxRegistrations ?? undefined,
    presetCountries: link.presetCountries ?? [],
    presetCategories: link.presetCategories ?? [],
    sections: link.sections ?? [],
  }
}

function EditLink({ link, onDone }: { link: RegistrationLink; onDone: () => void }) {
  const update = useUpdateLink(link.id)
  const [error, setError] = useState<string | null>(null)

  return (
    <LinkForm
      defaultValues={toFormValues(link)}
      submitLabel="Save changes"
      loading={update.isPending}
      error={error}
      slugLocked
      onCancel={onDone}
      onSubmit={(values) => {
        setError(null)
        // The slug is intentionally not sent: it may already be printed on a
        // flyer, and silently changing it would break every copy in the wild.
        const { slug: _slug, ...rest } = values
        update.mutate(rest, {
          onSuccess: onDone,
          onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Could not save'),
        })
      }}
    />
  )
}

function Registrants({ linkId }: { linkId: string }) {
  const { data, isLoading } = useQuery(registrantsQuery(linkId))
  const items = data?.items ?? []

  return (
    <Card className="overflow-hidden">
      <div className="border-b border-[var(--color-border)] p-4">
        <h2 className="text-sm font-semibold">Registrants</h2>
      </div>

      {isLoading && (
        <div className="space-y-2 p-8">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      )}
      {!isLoading && items.length === 0 && (
        <div className="flex flex-col items-center gap-3 p-8 text-center">
          <UserPlus className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Nobody has registered through this link yet.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--color-muted)]/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Phone</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Registered</th>
              </tr>
            </thead>
            <tbody>
              {items.map((client) => (
                <tr key={client.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-2">
                    <Link
                      to="/clients/$clientId"
                      params={{ clientId: client.id }}
                      className="font-medium text-[var(--color-primary)] hover:underline"
                    >
                      {client.fullName}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">{client.phone}</td>
                  <td className="px-4 py-2">
                    <Badge variant={statusTone(client.status)}>{statusLabel(client.status)}</Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-[var(--color-muted-foreground)]">
                    {formatDateTime(client.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
